import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient, useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft,
  Upload,
  Copy,
  Check,
  CheckCircle2,
  XCircle,
  Loader2,
  Eye,
  Globe,
} from "lucide-react";
import ReviewAssetRail from "../components/review-studio/ReviewAssetRail";
import ReviewAssetViewer from "../components/review-studio/ReviewAssetViewer";
import ReviewCommentPanel from "../components/review-studio/ReviewCommentPanel";

const statusPillConfig = {
  pending: { label: "Pending Review", className: "bg-muted text-muted-foreground border-border" },
  approved: { label: "Approved", className: "bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/20" },
  changes_requested: { label: "Changes Requested", className: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/20" },
};

export default function ReviewSessionDetail() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const fileInputRef = useRef(null);

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [comments, setComments] = useState([]);
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [pendingContext, setPendingContext] = useState(null);
  const [seekToTime, setSeekToTime] = useState(null);
  const [jumpToPage, setJumpToPage] = useState(null);
  const [goToYawPitch, setGoToYawPitch] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);

  const getFileUrl = useCallback((asset) => `${API}/review-studio/assets/${asset.id}/file?token=${token}`, [token]);

  const fetchSession = useCallback(async () => {
    try {
      const res = await apiClient.get(`/review-studio/sessions/${sessionId}`);
      setSession(res.data);
      setSelectedAssetId((prev) => prev || res.data.assets[0]?.id || null);
    } catch (e) {
      toast.error("Failed to load review session");
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => { fetchSession(); }, [fetchSession]);

  const fetchComments = useCallback(async () => {
    if (!selectedAssetId) return setComments([]);
    try {
      const res = await apiClient.get(`/review-studio/assets/${selectedAssetId}/comments`);
      setComments(res.data);
    } catch (e) {
      toast.error("Failed to load comments");
    }
  }, [selectedAssetId]);

  useEffect(() => {
    setPendingContext(null);
    setActiveCommentId(null);
    fetchComments();
  }, [fetchComments]);

  const selectedAsset = session?.assets.find((a) => a.id === selectedAssetId);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await apiClient.post(`/review-studio/sessions/${sessionId}/assets`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Asset uploaded");
      setSession((prev) => ({ ...prev, assets: [...prev.assets, res.data], asset_count: prev.asset_count + 1 }));
      setSelectedAssetId(res.data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleAddComment = async (draft) => {
    try {
      const res = await apiClient.post(`/review-studio/assets/${selectedAssetId}/comments`, draft);
      setComments((prev) => [...prev, res.data]);
      setPendingContext(null);
      setSession((prev) => ({
        ...prev,
        assets: prev.assets.map((a) => a.id === selectedAssetId ? { ...a, comment_count: (a.comment_count || 0) + 1 } : a),
      }));
    } catch (e) {
      toast.error("Failed to post comment");
    }
  };

  const handleResolveToggle = async (commentId, resolved) => {
    try {
      const res = await apiClient.patch(`/review-studio/comments/${commentId}`, { resolved });
      setComments((prev) => prev.map((c) => c.id === commentId ? res.data : c));
    } catch (e) {
      toast.error("Failed to update comment");
    }
  };

  const handleSelectComment = (commentId) => {
    setActiveCommentId(commentId);
    const c = comments.find((x) => x.id === commentId);
    if (!c) return;
    if (c.timestamp_seconds != null) setSeekToTime(c.timestamp_seconds);
    if (c.page_number != null) setJumpToPage(c.page_number);
    if (c.pin_yaw != null && c.pin_pitch != null) setGoToYawPitch({ yaw: c.pin_yaw, pitch: c.pin_pitch });
  };

  const handleSetStatus = async (status) => {
    setSavingStatus(true);
    try {
      const res = await apiClient.post(`/review-studio/assets/${selectedAssetId}/status`, { status });
      setSession((prev) => ({ ...prev, assets: prev.assets.map((a) => a.id === selectedAssetId ? res.data : a) }));
      toast.success(status === "approved" ? "Asset approved" : "Marked as changes requested");
    } catch (e) {
      toast.error("Failed to update status");
    } finally {
      setSavingStatus(false);
    }
  };

  const handleToggle360 = async () => {
    try {
      const res = await apiClient.patch(`/review-studio/assets/${selectedAssetId}`, { is_360: !selectedAsset.is_360 });
      setSession((prev) => ({ ...prev, assets: prev.assets.map((a) => a.id === selectedAssetId ? { ...a, ...res.data } : a) }));
    } catch (e) {
      toast.error("Failed to update asset");
    }
  };

  const clientUrl = session ? `${window.location.origin}/review/${session.client_token}` : "";

  if (loading) return <div className="p-6 text-sm text-muted-foreground text-center">Loading…</div>;
  if (!session) return <div className="p-6 text-sm text-muted-foreground text-center">Review session not found</div>;

  const statusCfg = selectedAsset ? (statusPillConfig[selectedAsset.status] || statusPillConfig.pending) : null;

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] -m-4 md:-m-6 lg:-m-8">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 md:px-6 py-3 border-b border-border shrink-0">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate("/review-studio")}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="min-w-0">
          <h1 className="text-sm font-semibold flex items-center gap-2 truncate">
            <Eye className="w-4 h-4 shrink-0" />
            {session.title}
          </h1>
          <p className="text-xs text-muted-foreground truncate">{session.program_name}</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1.5"
            onClick={async () => {
              await navigator.clipboard.writeText(clientUrl);
              setCopied(true);
              toast.success("Client review link copied");
              setTimeout(() => setCopied(false), 2000);
            }}
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? "Copied" : "Copy client link"}
          </Button>
          <input ref={fileInputRef} type="file" accept="image/*,video/*,.pdf" className="hidden" onChange={handleUpload} />
          <Button size="sm" className="h-8 text-xs gap-1.5" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {uploading ? "Uploading…" : "Upload Asset"}
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 min-h-0">
        {/* Asset rail */}
        <div className="w-64 border-r border-border p-3 overflow-y-auto shrink-0">
          {session.assets.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-8">Upload your first asset to start a review.</p>
          ) : (
            <ReviewAssetRail assets={session.assets} selectedId={selectedAssetId} onSelect={setSelectedAssetId} getFileUrl={getFileUrl} />
          )}
        </div>

        {/* Viewer */}
        <div className="flex-1 flex flex-col min-w-0 p-4 md:p-6 overflow-y-auto">
          {selectedAsset ? (
            <>
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-medium truncate">{selectedAsset.original_name}</span>
                  <Badge variant="outline" className="text-[10px] shrink-0">v{selectedAsset.version}</Badge>
                  {statusCfg && (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border shrink-0 ${statusCfg.className}`}>
                      {statusCfg.label}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {selectedAsset.file_type === "image" && (
                    <Button
                      size="sm"
                      variant={selectedAsset.is_360 ? "default" : "outline"}
                      className="h-7 text-xs gap-1.5"
                      onClick={handleToggle360}
                    >
                      <Globe className="w-3.5 h-3.5" />
                      {selectedAsset.is_360 ? "360° View" : "Mark as 360°"}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs gap-1.5 text-amber-700 border-amber-300 hover:bg-amber-50"
                    disabled={savingStatus}
                    onClick={() => handleSetStatus("changes_requested")}
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    Request Changes
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 text-xs gap-1.5 bg-green-600 hover:bg-green-700 text-white"
                    disabled={savingStatus}
                    onClick={() => handleSetStatus("approved")}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Approve
                  </Button>
                </div>
              </div>

              <div className="flex-1 flex items-center justify-center">
                <ReviewAssetViewer
                  asset={selectedAsset}
                  fileUrl={getFileUrl(selectedAsset)}
                  comments={comments}
                  activeCommentId={activeCommentId}
                  onPinDraft={(pin) => setPendingContext(
                    pin.yaw != null ? { pin_yaw: pin.yaw, pin_pitch: pin.pitch } : { pin_x: pin.x, pin_y: pin.y }
                  )}
                  onRequestTimeComment={(t) => setPendingContext({ timestamp_seconds: t })}
                  onRequestPageComment={(p) => setPendingContext({ page_number: p })}
                  onSelectComment={handleSelectComment}
                  seekToTime={seekToTime}
                  jumpToPage={jumpToPage}
                  goToYawPitch={goToYawPitch}
                />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
              Upload an asset to begin reviewing
            </div>
          )}
        </div>

        {/* Comments */}
        {selectedAsset && (
          <div className="w-80 border-l border-border p-4 shrink-0 min-h-0">
            <ReviewCommentPanel
              comments={comments}
              activeCommentId={activeCommentId}
              pendingContext={pendingContext}
              onSelect={handleSelectComment}
              onCancelPending={() => setPendingContext(null)}
              onSubmit={handleAddComment}
              onResolveToggle={handleResolveToggle}
            />
          </div>
        )}
      </div>
    </div>
  );
}
